import { Select } from "@/components/inputs/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAmenities } from "@/hooks/useAmenities";
import { Loader2 } from "lucide-react";
import { CheckboxGroup } from "@/components/checkbox-group";
import {
  STATUS_PRIVATE_OFFICE,
  STATUS_PRIVATE_OFFICE_NAMES,
} from "@/constants/status-private-office";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { useLocation } from "react-router-dom";
import { parseToCheckboxSections } from "../../utils/parseToCheckboxSections";
import { Checkbox } from "@/components/ui/checkbox";

const DEFAULT_DISTANCE = 30;
export interface Filters {
  minPrice?: number;
  maxPrice?: number;
  minCapacity?: number;
  maxCapacity?: number;
  privateOfficeStatus?: string[];
  amenitiesSlugs: string[];
  amenitiesSlugsOptional: string[];
  distance: number;
  unitCreditPrice?: number;
  capacityWeight?: number;
  priceWeight?: number;
  skipRecentUpdatesFilter?: boolean;
}

interface PainelFiltersListProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onApplyFilters?: (filters: Filters) => void;
  onSearchSubmit?: (filters: Filters) => void;
  dealId?: string | null;
}

const defaultFilters: Filters = {
  minPrice: undefined,
  maxPrice: undefined,
  minCapacity: undefined,
  maxCapacity: undefined,
  privateOfficeStatus: ["0"],
  amenitiesSlugs: [],
  amenitiesSlugsOptional: [],
  distance: DEFAULT_DISTANCE,
  unitCreditPrice: 80,
  capacityWeight: undefined,
  priceWeight: undefined,
  skipRecentUpdatesFilter: false,
};

const weightOptions = [
  { label: "Importante", value: "3" },
  { label: "Essencial", value: "2" },
  { label: "Não importa", value: "1" },
];

export const PainelFiltersList = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onSearchSubmit,
  dealId,
}: PainelFiltersListProps) => {
  const [localFilters, setLocalFilters] = useState<Filters>(filters);
  const {
    isLoading: isLoadingAmenities,
    amenities,
    handleSearchAmenities,
  } = useAmenities();
  const { userInfo } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  const parseNumericInput = (value: string): number | undefined => {
    if (value === "") return undefined;
    const parsed = parseInt(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);

    sendTrackDataServer({
      eventName: "VISION_SEARCH_FILTER_APPLIED",
      data: {
        deal_id: dealId ?? null,
        user_id: userInfo.user?.id ?? null,
        email: userInfo.profile?.email ?? null,
        capacity_max: localFilters.maxCapacity,
        capacity_min: localFilters.minCapacity,
        price_max: localFilters.maxPrice,
        price_min: localFilters.minPrice,
        amenities: localFilters.amenitiesSlugs,
        amenities_optional: localFilters.amenitiesSlugsOptional,
        status: localFilters.privateOfficeStatus,
        distance: localFilters.distance,
        unit_credit_price: localFilters.unitCreditPrice,
        capacity_weight: localFilters.capacityWeight,
        price_weight: localFilters.priceWeight,
        skip_recent_updates_filter: localFilters.skipRecentUpdatesFilter,
      },
      url: location.pathname,
      productType: "PO",
    });

    if (onSearchSubmit) {
      onSearchSubmit(localFilters);
    }

    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = { ...defaultFilters };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const handleCheckboxGroupChange = (
    fieldName:
      | "amenitiesSlugs"
      | "amenitiesSlugsOptional"
      | "privateOfficeStatus",
  ) => {
    return (
      selectedOptions: { sectionTitle: string; selectedIds: string[] }[],
    ) => {
      const allSelectedIds = selectedOptions.flatMap(
        (section) => section.selectedIds,
      );

      const newFilters = {
        ...localFilters,
        [fieldName]: allSelectedIds,
      };
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    };
  };

  useEffect(() => {
    handleSearchAmenities();
  }, []);

  const statusOptions = useMemo(
    () => [
      {
        title: "Status",
        options: [
          {
            id: STATUS_PRIVATE_OFFICE.AVAILABLE.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.AVAILABLE.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.AVAILABLE.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.NOT_AVAILABLE.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.OCCUPED_WITH_WOBA_CLIENT.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.OCCUPED_WITH_WOBA_CLIENT.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.OCCUPED_WITH_WOBA_CLIENT.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.AVAILABLE_WITHOUT_TP.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.AVAILABLE_WITHOUT_TP.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.AVAILABLE_WITHOUT_TP.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.NOT_AVAILABLE_WITHOUT_TP.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.NOT_AVAILABLE_WITHOUT_TP.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.NOT_AVAILABLE_WITHOUT_TP.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.RESERVED.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.RESERVED.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.RESERVED.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.DELETED.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.DELETED.title,
            checked: localFilters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.DELETED.toString(),
            ),
          },
        ],
        columns: 2 as const,
      },
    ],
    [localFilters.privateOfficeStatus],
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-[40rem] bg-white">
                  <div className="flex justify-between pt-4 px-4">
                    <div className="flex gap-2 flex-col px-4">
                      <Dialog.Title className="text-xl font-semibold text-zinc-950">
                        Filtros
                      </Dialog.Title>
                      <p className="text-sm text-zinc-500">
                        Use os filtros para encontrar espaços que realmente
                        fazem sentido para você. Quanto mais preciso, melhores
                        os resultados.
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label="Fechar painel"
                      className=" border border-zinc-200 hover:bg-zinc-100 p-2 h-9 w-9 rounded-md text-zinc-950 flex items-center justify-center"
                      onClick={onClose}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="">
                    <Separator className="mt-4" />
                  </div>
                  <div className="flex h-[calc(100vh-14rem)] flex-col overflow-y-scroll  w-full shadow-xl">
                    {isLoadingAmenities ? (
                      <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : (
                      <div className="flex-1 px-9 py-9 gap-4">
                        <div className="flex flex-col gap-2 p-4">
                          <Label>Capacidade</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={localFilters.minCapacity ?? ""}
                              placeholder="Mínimo"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) =>
                                updateFilter(
                                  "minCapacity",
                                  parseNumericInput(e.target.value),
                                )
                              }
                            />
                            <Input
                              type="number"
                              value={localFilters.maxCapacity ?? ""}
                              placeholder="Máximo"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) =>
                                updateFilter(
                                  "maxCapacity",
                                  parseNumericInput(e.target.value),
                                )
                              }
                            />
                          </div>
                          <Select
                            options={weightOptions}
                            placeholder="Definir relevância na busca"
                            value={(
                              localFilters.capacityWeight || 0
                            ).toString()}
                            onChange={(val) =>
                              updateFilter("capacityWeight", Number(val))
                            }
                            onClear={() =>
                              updateFilter("capacityWeight", undefined)
                            }
                            emptyValue="0"
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="unit-credit-price">
                              Preço do crédito
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="bg-black text-white"
                                >
                                  Preço por crédito a ser considerado no cálculo
                                  do valor mensal
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <Input
                            id="unit-credit-price"
                            type="number"
                            placeholder="0"
                            step="0.01"
                            value={localFilters.unitCreditPrice ?? ""}
                            max={80}
                            min={0}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (e.target.value === "") {
                                updateFilter("unitCreditPrice", undefined);
                              } else if (!isNaN(value)) {
                                if (value > 80) {
                                  updateFilter("unitCreditPrice", 80);
                                } else if (value < 0) {
                                  updateFilter("unitCreditPrice", 0);
                                } else {
                                  updateFilter("unitCreditPrice", value);
                                }
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <Label>Preço mensal</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={localFilters.minPrice ?? ""}
                              placeholder="Mínimo"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) =>
                                updateFilter(
                                  "minPrice",
                                  parseNumericInput(e.target.value),
                                )
                              }
                            />
                            <Input
                              type="number"
                              value={localFilters.maxPrice ?? ""}
                              placeholder="Máximo"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) =>
                                updateFilter(
                                  "maxPrice",
                                  parseNumericInput(e.target.value),
                                )
                              }
                            />
                          </div>
                          <Select
                            options={weightOptions}
                            placeholder="Definir relevância na busca"
                            value={(localFilters.priceWeight || 0).toString()}
                            onChange={(val) =>
                              updateFilter("priceWeight", Number(val))
                            }
                            onClear={() =>
                              updateFilter("priceWeight", undefined)
                            }
                            emptyValue="0"
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="distance">Distância - Km</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="bg-black text-white"
                                >
                                  Distância máxima desejada em quilometros da
                                  localização escolhida até os ativos
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <Input
                            id="distance"
                            type="number"
                            value={localFilters.distance ?? ""}
                            placeholder={DEFAULT_DISTANCE.toString()}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) =>
                              updateFilter(
                                "distance",
                                parseNumericInput(e.target.value) ??
                                  DEFAULT_DISTANCE,
                              )
                            }
                            style={{ width: "50%" }}
                          />
                        </div>

                        <Label className="ml-4">Amenidades</Label>
                        <Tabs className="p-4" defaultValue="amenities">
                          <TabsList className="w-full bg-[#F4F4F5]">
                            <TabsTrigger
                              className="w-full text-[#71717A]"
                              value="amenities"
                            >
                              Obrigatórias
                            </TabsTrigger>
                            <TabsTrigger
                              className="w-full text-[#71717A]"
                              value="amenities-optional"
                            >
                              Desejáveis
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="amenities">
                            <div className="flex flex-col gap-2 mt-4">
                              <CheckboxGroup
                                sections={parseToCheckboxSections(
                                  amenities ?? [],
                                  localFilters.amenitiesSlugs,
                                )}
                                onChange={handleCheckboxGroupChange(
                                  "amenitiesSlugs",
                                )}
                                disabledOptionIds={
                                  localFilters.amenitiesSlugsOptional
                                }
                                className="border border-zinc-200 rounded-md p-4"
                              />
                            </div>
                          </TabsContent>
                          <TabsContent value="amenities-optional">
                            <div className="flex flex-col gap-2 mt-4">
                              <CheckboxGroup
                                sections={parseToCheckboxSections(
                                  amenities ?? [],
                                  localFilters.amenitiesSlugsOptional,
                                )}
                                disabledOptionIds={localFilters.amenitiesSlugs}
                                onChange={handleCheckboxGroupChange(
                                  "amenitiesSlugsOptional",
                                )}
                                className="border border-zinc-200 rounded-md p-4"
                              />
                            </div>
                          </TabsContent>
                        </Tabs>

                        <div className="flex flex-col gap-2 p-4">
                          <CheckboxGroup
                            sections={statusOptions}
                            onChange={handleCheckboxGroupChange(
                              "privateOfficeStatus",
                            )}
                            showClearButton={false}
                            className="border border-zinc-200 rounded-md p-4"
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <div className="border border-zinc-200 rounded-md p-4">
                            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                              <span>
                                Atualizações recentes consideram mudança no
                                status do item nos últimos 30 dias, independente
                                de para qual valor foi alterado. Por padrão, não
                                são exibidos itens que não foram alterados nos
                                últimos 30 dias.
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="skip-recent-updates"
                                checked={localFilters.skipRecentUpdatesFilter}
                                onCheckedChange={(checked) =>
                                  updateFilter(
                                    "skipRecentUpdatesFilter",
                                    checked === true,
                                  )
                                }
                              />
                              <Label
                                htmlFor="skip-recent-updates"
                                className="text-sm font-medium text-gray-700 cursor-pointer"
                              >
                                Incluir ativos sem atualizações recentes
                              </Label>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 p-4 border-zinc-200 justify-end  flex-col bg-white">
                    <Separator className="mt-4" />
                    <div className="flex gap-3 p-4 border-zinc-200 justify-end bg-white">
                      <Button onClick={handleResetFilters} variant="outline">
                        Limpar filtros
                      </Button>
                      <Button onClick={handleApplyFilters}>
                        Aplicar filtros
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
